import React from 'react';
import {
  InsuranceAccountDTO,
  InsuranceDTO,
  InsurancePeriodType,
  InsuranceType,
} from '../../api/useInsuranceQuery';
import { InsuranceTable } from '../../components/InsuranceTable/InsuranceTable';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { ColumnHeader } from '../../../OtherAssets/OtherAssets.meta';
import { InsuranceToDelete, InsuranceToUpsert } from '../../Insurance.meta';
import { TableActions } from '../../components/TableActions/TableActions';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { useIsPdf } from '../../../../FinancialPlanOPL/hooks/useIsPdf';
import { ClickableTableRow } from '../../../../CommonComponents/OPL/HtmlTable/HtmlTable';

const columnHeaders: ColumnHeader[] = [
  { text: 'Name' },
  { text: 'Owner' },
  { text: 'Daily Amount', align: 'right' },
  { text: 'Coverage Period' },
  { text: 'Elimination Period' },
  { text: 'Premium', align: 'right' },
];

interface Props {
  data: InsuranceDTO;
  setInsuranceToUpsert(insurance: InsuranceToUpsert): void;
  setInsuranceToDelete(insurance: InsuranceToDelete): void;
  onClone(insurance: InsuranceAccountDTO): void;
  workflowProps?: WorkflowComponentProps;
}

export const LongTermCareInsurance = ({
  data,
  setInsuranceToUpsert,
  setInsuranceToDelete,
  onClone,
  workflowProps,
}: Props) => {
  const items = data.Items.LongTermCare || [];

  const isPrint = useIsPdf();

  return (
    <InsuranceTable
      title="Long Term Care Insurance Policies"
      isEmpty={items.length === 0}
      columnHeaders={columnHeaders}
      onAddInsurance={() => setInsuranceToUpsert({ InsuranceType: InsuranceType.LongTermCare })}
      workflowProps={workflowProps}
    >
      {items.map(insurance => (
        <ClickableTableRow
          variant="clickable"
          key={insurance.AccountId}
          onClick={() => {
            setInsuranceToUpsert(insurance);
          }}
        >
          <td>
            <Text fontWeight={500} textAlign="left" width="100%">
              {insurance.Description}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {insurance.AccountOwner}
            </Text>
          </td>

          <td>
            <Text variant="tabular" fontWeight={500} textAlign="right">
              {insurance.LongTermCareDailyBenefitAmount !== null
                ? formatCurrency(insurance.LongTermCareDailyBenefitAmount)
                : ''}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {insurance.LongTermCareCoveragePeriod
                ? insurance.LongTermCareCoveragePeriod.PeriodType === InsurancePeriodType.Lifetime
                  ? insurance.LongTermCareCoveragePeriod.Label
                  : insurance.LongTermCareCoveragePeriod.DisplayString
                : ''}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {insurance.LongTermCareEliminationPeriod
                ? insurance.LongTermCareEliminationPeriod.DisplayString
                : ''}
            </Text>
          </td>

          <td>
            <Text variant="tabular" fontWeight={500} textAlign="right">
              {insurance.Premium !== null ? formatCurrency(insurance.Premium) : ''}
            </Text>
          </td>

          {!isPrint && (
            <TableActions
              insuranceName={insurance.Description}
              onEdit={() => setInsuranceToUpsert(insurance)}
              onDelete={() => setInsuranceToDelete({ id: insurance.AccountId })}
              onClone={() => onClone(insurance)}
            />
          )}
        </ClickableTableRow>
      ))}
    </InsuranceTable>
  );
};
